import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface PatientFileRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  kind: string | null;
  filename: string | null;
  url: string | null;
  tag: string | null;
  mime_type: string | null;
  description: string | null;
  starred: number | null; // 0 or 1
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientNoteRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  title: string | null;
  body: string | null;
  note_type: string | null; // 'general' | 'clinical' | 'follow_up' | 'admin'
  visibility: string | null; // 'team' | 'private' | 'front_desk'
  alert_level: string | null; // 'none' | 'info' | 'warning' | 'critical'
  author_user_id: string | null;
  author_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientPrescriptionRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  item_name: string | null;
  item_details: string | null;
  // PRD.189 — optional Medicine inventory reference + snapshots
  inventory_item_id: string | null;
  free_text_name: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  generic_name_snapshot: string | null;
  brand_name_snapshot: string | null;
  strength_snapshot: string | null;
  dosage_form_snapshot: string | null;
  route_snapshot: string | null;
  unit_snapshot: string | null;
  default_instruction_snapshot: string | null;
  default_dosage_snapshot: string | null;
  usage_snapshot: string | null;
  dose_snapshot: string | null;
  dose_unit_snapshot: string | null;
  frequency_snapshot: string | null;
  duration_snapshot: string | null;
  intake_timing: string | null; // 'before_food' | 'after_food' | 'with_food' | 'empty_stomach' | 'anytime'
  dose_quantity: string | null;
  schedule_morning: number | null; // 0 or 1
  schedule_afternoon: number | null;
  schedule_night: number | null;
  duration_value: number | null;
  duration_unit: string | null; // 'days' | 'weeks' | 'months'
  instructions: string | null;
  sort_order: number | null;
  author_user_id: string | null;
  author_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientFormRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  name: string | null;
  tag: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

export function qPatientFiles(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_files', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_files WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_files'],
  };
}

export function qPatientFilesByEncounter(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['patient_files', 'by-encounter', patientId, encounterId] as const,
    sql: `SELECT * FROM patient_files WHERE patient_id = ? AND encounter_id = ? ORDER BY created_at DESC`,
    params: [patientId, encounterId],
    watch: ['patient_files'],
  };
}

export function qPatientNotes(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_notes WHERE patient_id = ? ORDER BY updated_at DESC`,
    params: [patientId],
    watch: ['patient_notes'],
  };
}

export function qNotesByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_notes WHERE encounter_id = ? ORDER BY created_at DESC`,
    params: [encounterId],
    watch: ['patient_notes'],
  };
}

export function qPatientAlertNotes(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'alerts', patientId] as const,
    sql: `SELECT * FROM patient_notes
          WHERE patient_id = ?
            AND alert_level IN ('info', 'warning', 'critical')
            AND visibility IN ('team', 'front_desk')
          ORDER BY created_at DESC
          LIMIT 3`,
    params: [patientId],
    watch: ['patient_notes'],
  };
}

export function qNotesPreviewByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'preview-by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_notes WHERE encounter_id = ? ORDER BY created_at DESC LIMIT 3`,
    params: [encounterId],
    watch: ['patient_notes'],
  };
}

export function qPrescriptionsByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_prescriptions', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_prescriptions WHERE encounter_id = ? ORDER BY sort_order ASC, created_at ASC`,
    params: [encounterId],
    watch: ['patient_prescriptions'],
  };
}

export function qPrescriptionsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_prescriptions', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_prescriptions WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_prescriptions'],
  };
}

export function qPatientForms(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_forms', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_forms WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_forms'],
  };
}
